import { createAsyncThunk } from '@reduxjs/toolkit'
import { wikiAPI } from '../../utils/supabase'

// === SPACE THUNKS ===

export const fetchSpaces = createAsyncThunk(
  'wiki/fetchSpaces',
  async (userId, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.getAllSpaces(userId)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const createSpace = createAsyncThunk(
  'wiki/createSpace',
  async ({ userId, spaceData }, { getState, rejectWithValue }) => {
    // Calculate display_order as count of existing spaces (puts new space at end)
    const state = getState()
    const existingSpaces = state.wiki.spaces
    const displayOrder = existingSpaces.length

    const { data, error } = await wikiAPI.createSpace(userId, {
      ...spaceData,
      display_order: displayOrder
    })
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const updateSpace = createAsyncThunk(
  'wiki/updateSpace',
  async ({ spaceId, updates }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.updateSpace(spaceId, updates)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const deleteSpace = createAsyncThunk(
  'wiki/deleteSpace',
  async (spaceId, { rejectWithValue }) => {
    const { error } = await wikiAPI.deleteSpace(spaceId)
    if (error) return rejectWithValue(error.message)
    return spaceId
  }
)

// Archive a space and cascade-archive its pages (soft, reversible)
export const archiveSpace = createAsyncThunk(
  'wiki/archiveSpace',
  async ({ spaceId, userId }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.archiveSpace(spaceId, userId)
    if (error) return rejectWithValue(error.message)
    // data = { space, page_ids }
    return { spaceId, pageIds: data?.page_ids || [], space: data?.space || null }
  }
)

// Restore an archived space and its archived pages
export const restoreSpace = createAsyncThunk(
  'wiki/restoreSpace',
  async ({ spaceId, userId }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.restoreSpace(spaceId, userId)
    if (error) return rejectWithValue(error.message)
    // data = { space, pages }
    return { space: data?.space || null, pages: data?.pages || [] }
  }
)

// Fetch archived spaces (on-demand for the Archived view)
export const fetchArchivedSpaces = createAsyncThunk(
  'wiki/fetchArchivedSpaces',
  async (userId, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.getArchivedSpaces(userId)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const updateSpaceCover = createAsyncThunk(
  'wiki/updateSpaceCover',
  async ({ spaceId, coverImageUrl, coverGradient }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.updateSpaceCover(spaceId, { coverImageUrl, coverGradient })
    if (error) return rejectWithValue(error.message)
    return data
  }
)

// Upload space cover image (handles upload + space update)
export const uploadSpaceCoverImage = createAsyncThunk(
  'wiki/uploadSpaceCoverImage',
  async ({ userId, spaceId, file }, { rejectWithValue }) => {
    // Upload file to storage
    const { data: uploadData, error: uploadError } = await wikiAPI.uploadSpaceCoverImage(userId, spaceId, file)
    if (uploadError) return rejectWithValue(uploadError.message)

    // Update space with new cover image URL
    const { data: spaceData, error: spaceError } = await wikiAPI.updateSpaceCover(spaceId, {
      coverImageUrl: uploadData.publicUrl
    })
    if (spaceError) {
      // Clean up uploaded file if space update fails
      await wikiAPI.deleteSpaceCoverImage(uploadData.storagePath)
      return rejectWithValue(spaceError.message)
    }

    return spaceData
  }
)

export const updateSpaceOrder = createAsyncThunk(
  'wiki/updateSpaceOrder',
  async ({ spaces }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.updateSpaceOrder(spaces)
    if (error) return rejectWithValue(error.message)
    return data
  }
)
